import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeNL from '@angular/common/locales/nl';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NuMonacoEditorModule } from '@ng-util/monaco-editor';
import { MarkdownModule } from 'ngx-markdown';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { ToastrModule } from 'ngx-toastr';

import {
  AUTH_ROUTES,
  authRequestInterceptor,
  responseInterceptor,
} from '@seahorse/auth';
import {
  ApplicationInsightsService,
  BUILD_NO,
  EnvService,
  envServiceProvider,
  NotificationService,
  PreloaderServiceBase,
  SeahorseTranslateModule,
} from '@seahorse/common';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import { ColorSchemeService, Shipm8Preset } from '@seahorse/theme';
import { SEAHORSE_BUILD_NO } from '../build';

// Org chart token overrides for the subscriptions flow chart (super-user >
// customers > subscriptions). Literal hex/px values: this preset generates
// global CSS, so it can't use the --sm-* custom properties. The rest of the
// flow chart styling is the central `.flow-chart` class in @seahorse/theme
// (_flow-chart.scss); keep the two visually in line.
const orgChartOverrides = {
  organizationchart: {
    root: {
      gutter: '0 8px',
    },
    node: {
      background: '#ffffff',
      hoverBackground: '#f4f7f9',
      selectedBackground: '#fef4e6',
      borderColor: '#e3eaf0',
      color: '#0b1622',
      hoverColor: '#0b1622',
      selectedColor: '#0b1622',
      borderRadius: '6px',
      padding: '0',
      toggleablePadding: '0 0 12px 0',
    },
    nodeToggleButton: {
      background: '#ffffff',
      hoverBackground: '#f4f7f9',
      borderColor: '#c7d3de',
      color: '#5b6b7a',
      hoverColor: '#0c365a',
      size: '20px',
      borderRadius: '50%',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '#0c365a',
        offset: '2px',
        shadow: 'none',
      },
    },
    connector: {
      color: '#5e7d99',
      height: '16px',
      borderRadius: '0',
    },
  },
};

const SuperUserPreset = definePreset(Shipm8Preset, {
  components: orgChartOverrides,
});
import { SuEnvService } from './shared/services/env.service';
import { SuperUserAppComponent } from './super-user-app.component';
import { SuperUserAppRoutingModule } from './super-user-routing.module';
import { PreloaderService } from './core/services/preloader.service';
import { ToastrNotificationService } from '@seahorse/ui';
import { SuUserService } from './core/services/user.service';
import { ProxyService, UsersDataService } from '@seahorse/domain';
import { ProxyServices } from './shared/services/proxy.service';
import { SH_DATE_PICKER_OPTIONS } from '@seahorse/components';

export function initializeApp() {
  return () => registerLocaleData(localeNL, 'nl-NL');
}

@NgModule({
  declarations: [SuperUserAppComponent],
  imports: [
    FormsModule,
    NgbModule,
    BrowserModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      closeButton: true,
      newestOnTop: true,
      progressBar: true,
      positionClass: 'toast-bottom-right',
    }),
    SeahorseTranslateModule.forRoot(),
    ReactiveFormsModule,
    NuMonacoEditorModule.forRoot(),
    MarkdownModule.forRoot(),

    SuperUserAppRoutingModule,
  ],
  providers: [
    ApplicationInsightsService,
    envServiceProvider(SuEnvService),
    provideHttpClient(
      withInterceptors([authRequestInterceptor, responseInterceptor])
    ),
    { provide: EnvService, useExisting: SuEnvService },
    provideAppInitializer(() => {
      const initializerFn = initializeApp();
      return initializerFn();
    }),
    // Start the colour scheme service with the app, so it follows the OS
    // light/dark setting live on every page, not only once a page injects it.
    provideAppInitializer(() => {
      inject(ColorSchemeService);
    }),
    { provide: BUILD_NO, useValue: SEAHORSE_BUILD_NO },
    // Every date field: shown and typed as dd-mm-yyyy (mask 99-99-9999).
    {
      provide: SH_DATE_PICKER_OPTIONS,
      useValue: { dateFormat: 'dd-mm-yy', inputMask: '99-99-9999' },
    },
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    {
      provide: AUTH_ROUTES,
      useValue: {
        login: 'account/login',
        unauthorized: 'not-authorized',
      },
    },
    {
      provide: PreloaderServiceBase,
      useFactory: () => inject(PreloaderService),
    },
    {
      provide: NotificationService,
      useFactory: () => inject(ToastrNotificationService),
    },
    { provide: ProxyService, useFactory: () => inject(ProxyServices) },
    { provide: UsersDataService, useFactory: () => inject(SuUserService) },
    providePrimeNG({
      theme: {
        preset: SuperUserPreset,
        options: {
          darkModeSelector: '.sm-dark',
        },
      },
    }),
  ],
  bootstrap: [SuperUserAppComponent],
})
export class SuperUserAppModule {}
